/**
 * VM Manager UI - Production Entry Point
 *
 * Implements: BPMN Activity "Authenticate User" → "Display Dashboard"
 * ArchiMate: Application Function (User Authentication & Dashboard)
 *
 * Production authentication flow:
 * 1. Cloudflare Access validates user via Google Workspace SSO
 * 2. JWT extracted from CF-Access-JWT-Assertion header
 * 3. Role determined from email or group membership
 * 4. Dashboard displays actual logged-in user information
 *
 * Special role assignments:
 * - jorgen.scheel@ecobyte.net: superadmin (complete system access)
 * - Others: Role based on Google Workspace group membership
 *
 * @module vm-manager-ui
 */

import { createAuthenticatedUser, getRoleDescription } from '@proxmox-control-plane/rbac';
import type {
  AuthenticatedUser,
  CloudflareAccessUser,
} from '@proxmox-control-plane/common-types';

// ============================================================================
// Authentication
// ============================================================================

/**
 * Extracts user from Cloudflare Access JWT (production) or simulates for development
 *
 * BPMN Activity: "Extract User Identity"
 * ArchiMate: Application Function (Identity Extraction)
 *
 * Production: Reads CF-Access-JWT-Assertion header (provided by Cloudflare Pages)
 * Development: Simulates authentication for local testing
 *
 * @returns Cloudflare Access user identity
 */
async function getCurrentUser(): Promise<CloudflareAccessUser> {
  // Production: Cloudflare Pages provides JWT via header
  // In browser context, we'll need to make an API call to get user info
  // For now, simulate with a minimal user for the logged-in session

  // In production deployment, Cloudflare Access will have already authenticated
  // the user before they reach this page. The backend API would extract the JWT
  // and return user information.

  // Development mode: Simulate jorgen.scheel@ecobyte.net as superadmin
  if (import.meta.env.DEV) {
    // Simulate 1 second delay for auth
    await new Promise((resolve) => setTimeout(resolve, 1000));

    return {
      id: 'dev-superadmin-001',
      email: 'jorgen.scheel@ecobyte.net',
      name: 'Jørgen Scheel',
      groups: ['vmfactory-admins'], // Would also get superadmin via email match
    };
  }

  // Production: Make API call to get authenticated user info from backend
  // The backend Worker will read CF-Access-JWT-Assertion and return user details
  try {
    const response = await fetch('/api/auth/me', {
      method: 'GET',
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error(`Authentication failed: ${response.status} ${response.statusText}`);
    }

    const userData = (await response.json()) as { user: CloudflareAccessUser };
    return userData.user;
  } catch (error) {
    console.error('Failed to fetch user info:', error);
    throw new Error(
      'Unable to authenticate. Please ensure Cloudflare Access is configured correctly.'
    );
  }
}

// ============================================================================
// UI Rendering
// ============================================================================

/**
 * Renders user information in sidebar
 */
function renderSidebarUser(user: AuthenticatedUser): void {
  const sidebarUserInfo = document.getElementById('sidebar-user-info');
  if (!sidebarUserInfo) return;

  sidebarUserInfo.innerHTML = `
    <div class="user-name">${user.identity.name || user.identity.email}</div>
    <div class="user-role">${user.role}</div>
  `;
}

/**
 * Renders dashboard welcome section
 */
function renderWelcomeSection(user: AuthenticatedUser): void {
  const welcomeName = document.getElementById('welcome-name');
  const welcomeRole = document.getElementById('welcome-role');

  if (welcomeName) {
    welcomeName.textContent = user.identity.name || user.identity.email.split('@')[0] || 'User';
  }

  if (welcomeRole) {
    welcomeRole.textContent = getRoleDescription(user.role);
  }
}

/**
 * Renders user details card
 */
function renderUserDetails(user: AuthenticatedUser): void {
  const userDetailsEl = document.getElementById('user-details');
  if (!userDetailsEl) return;

  const authenticatedAt = user.sessionMetadata?.authenticatedAt
    ? new Date(user.sessionMetadata.authenticatedAt).toLocaleString()
    : 'N/A';

  userDetailsEl.innerHTML = `
    <dt>Name</dt>
    <dd>${user.identity.name || 'N/A'}</dd>
    <dt>Email</dt>
    <dd>${user.identity.email}</dd>
    <dt>Role</dt>
    <dd><span class="role-badge ${user.role}">${user.role.toUpperCase()}</span></dd>
    <dt>Groups</dt>
    <dd>${user.identity.groups.join(', ') || 'None'}</dd>
    <dt>Authenticated</dt>
    <dd>${authenticatedAt}</dd>
  `;
}

/**
 * Renders role capabilities card
 */
function renderRoleCapabilities(user: AuthenticatedUser): void {
  const capabilitiesList = document.getElementById('capabilities-list');
  if (!capabilitiesList) return;

  // Group permissions by category
  const categories: Record<string, string[]> = {
    'User Management': [],
    'VM Operations': [],
    'Infrastructure': [],
  };

  for (const permission of Array.from(user.permissions)) {
    if (permission.startsWith('user.') || permission.startsWith('role.') || permission.startsWith('system.')) {
      categories['User Management']!.push(permission);
    } else if (permission.startsWith('vm.')) {
      categories['VM Operations']!.push(permission);
    } else {
      categories['Infrastructure']!.push(permission);
    }
  }

  let html = '';
  for (const [category, perms] of Object.entries(categories)) {
    if (perms.length > 0) {
      html += `<div class="capability-category">
        <h4>${category}</h4>
        <ul>`;
      for (const perm of perms) {
        html += `<li><span class="material-icons">check_circle</span>${perm}</li>`;
      }
      html += `</ul></div>`;
    }
  }

  capabilitiesList.innerHTML = html;
}

/**
 * Sets up navigation based on permissions
 */
function setupNavigation(user: AuthenticatedUser): void {
  // Create VM nav
  const navCreateVM = document.getElementById('nav-create-vm');
  if (navCreateVM) {
    if (user.permissions.has('vm.create')) {
      navCreateVM.classList.remove('disabled');
      navCreateVM.addEventListener('click', (e) => {
        e.preventDefault();
        showCreateVMView(user);
      });
    } else {
      navCreateVM.classList.add('disabled');
    }
  }

  // Enable/disable nav items based on permissions
  const navUserManagement = document.getElementById('nav-user-management');

  if (navUserManagement) {
    if (user.permissions.has('user.view')) {
      navUserManagement.classList.remove('disabled');
      navUserManagement.addEventListener('click', (e) => {
        e.preventDefault();
        showUserManagementModule(user);
      });
    } else {
      navUserManagement.classList.add('disabled');
    }
  }

  // Dashboard is always active on load
  const navDashboard = document.getElementById('nav-dashboard');
  navDashboard?.addEventListener('click', (e) => {
    e.preventDefault();
    showDashboard(user);
  });

  // Logout handler
  const logoutBtn = document.getElementById('logout-btn');
  if (logoutBtn) {
    logoutBtn.addEventListener('click', () => {
      // In production, redirect to Cloudflare Access logout
      if (import.meta.env.DEV) {
        alert('Logout (in production: redirects to Cloudflare Access logout)');
        window.location.reload();
      } else {
        // Cloudflare Access logout URL
        window.location.href = '/cdn-cgi/access/logout';
      }
    });
  }
}

/**
 * Shows dashboard view
 */
function showDashboard(user: AuthenticatedUser): void {
  // Hide all views
  document.querySelectorAll('.view').forEach((view) => view.classList.add('hidden'));

  // Show dashboard
  const dashboardView = document.getElementById('dashboard-view');
  dashboardView?.classList.remove('hidden');

  // Update active nav
  document.querySelectorAll('.nav-item').forEach((item) => item.classList.remove('active'));
  document.getElementById('nav-dashboard')?.classList.add('active');

  // Render dashboard content
  renderWelcomeSection(user);
  renderUserDetails(user);
  renderRoleCapabilities(user);
}

/**
 * Shows User Management module
 */
function showUserManagementModule(user: AuthenticatedUser): void {
  // Hide all views
  document.querySelectorAll('.view').forEach((view) => view.classList.add('hidden'));

  // Show user management view
  const userManagementView = document.getElementById('user-management-view');
  userManagementView?.classList.remove('hidden');

  // Update active nav
  document.querySelectorAll('.nav-item').forEach((item) => item.classList.remove('active'));
  document.getElementById('nav-user-management')?.classList.add('active');

  // Render user management content
  renderUserManagementModule(user);
}

/**
 * Renders User Management module content
 */
function renderUserManagementModule(user: AuthenticatedUser): void {
  const canManage = user.permissions.has('user.manage');
  const canAssignRoles = user.permissions.has('role.assign');

  const userManagementContent = document.getElementById('user-management-content');
  if (!userManagementContent) return;

  userManagementContent.innerHTML = `
    <div class="module-header">
      <h2>User & Role Management</h2>
      ${canManage ? '<button class="btn-primary" id="btn-add-user"><span class="material-icons">person_add</span>Add User</button>' : ''}
    </div>

    <div class="card">
      <div class="card-header">
        <span class="material-icons">people</span>
        <h3>Users</h3>
      </div>
      <div class="card-body">
        ${
          canManage
            ? '<p class="info-message"><span class="material-icons">info</span>User management functionality will be implemented with API integration in the next phase.</p>'
            : '<p class="info-message"><span class="material-icons">info</span>You have view-only access to user information.</p>'
        }

        <div class="user-list-placeholder">
          <div class="placeholder-item">
            <span class="material-icons">person</span>
            <div class="placeholder-details">
              <strong>${user.identity.email}</strong> (You)
              <br>
              <span class="role-badge ${user.role}">${user.role.toUpperCase()}</span>
            </div>
          </div>
        </div>

        ${
          canAssignRoles
            ? `
        <div class="role-info">
          <h4>Available Roles</h4>
          <dl class="role-descriptions">
            <dt><span class="role-badge superadmin">SUPERADMIN</span></dt>
            <dd>Complete system access including user and role management (${user.identity.email})</dd>

            <dt><span class="role-badge admin">ADMIN</span></dt>
            <dd>Full access to all VM operations and system configuration</dd>

            <dt><span class="role-badge operations">OPERATIONS</span></dt>
            <dd>Operational control - can start, stop, and monitor VMs</dd>

            <dt><span class="role-badge developer">DEVELOPER</span></dt>
            <dd>Development access - can create and manage own VMs</dd>

            <dt><span class="role-badge viewer">VIEWER</span></dt>
            <dd>Read-only access to view VMs and node status</dd>
          </dl>
        </div>
        `
            : ''
        }
      </div>
    </div>
  `;

  // Add user button handler
  if (canManage) {
    const btnAddUser = document.getElementById('btn-add-user');
    btnAddUser?.addEventListener('click', () => {
      alert(
        'Add User functionality will be implemented with:\n' +
          '- User creation form\n' +
          '- Role assignment\n' +
          '- API integration with Cloudflare D1\n' +
          '- Audit logging'
      );
    });
  }
}

/**
 * Sets build information in footer
 */
function setBuildInfo(): void {
  const buildIdEl = document.getElementById('build-id');
  if (!buildIdEl) return;

  const buildId = (import.meta.env['VITE_BUILD_ID'] as string | undefined) || 'development';
  const repoUrl =
    (import.meta.env['VITE_REPO_URL'] as string | undefined) ||
    'https://github.com/jorgenscheel/proxmox-control-plane';
  const environment = (import.meta.env['VITE_ENVIRONMENT'] as string | undefined) || 'development';

  if (buildId === 'development' || buildId === 'dev') {
    buildIdEl.textContent = buildId;
  } else {
    const commitSha = (import.meta.env['VITE_COMMIT_SHA'] as string | undefined) || buildId;
    const commitUrl = `${repoUrl}/commit/${commitSha}`;
    buildIdEl.innerHTML = `<a href="${commitUrl}" target="_blank" rel="noopener noreferrer" style="color: var(--nexus-blue); text-decoration: none;">${buildId}</a>`;
  }

  if (environment === 'production') {
    const buildInfoEl = document.getElementById('build-info');
    if (buildInfoEl) {
      const buildDate = (import.meta.env['VITE_BUILD_DATE'] as string | undefined) || 'N/A';
      buildInfoEl.title = `Environment: ${environment} | Build Date: ${buildDate}`;
    }
  }
}

// ============================================================================
// Create VM Wizard
// ============================================================================

/**
 * Template configuration for VM creation wizard.
 * Maps to Cloud-Init template VMIDs deployed on each Proxmox node.
 */
const VM_TEMPLATES = {
  server: {
    label: 'Ubuntu CLI',
    description: 'Ubuntu 24.04 LTS Server (headless)',
    vmids: { 'pmhs-03': 9000, 'pmhs-04': 9001 } as Record<string, number>,
    defaults: { memory: 2048, cores: 2 },
  },
  desktop: {
    label: 'Ubuntu UI',
    description: 'Lubuntu 24.04 LTS (LXQt desktop)',
    vmids: { 'pmhs-03': 9004, 'pmhs-04': 9005 } as Record<string, number>,
    defaults: { memory: 4096, cores: 2 },
  },
} as const;

type TemplateKey = keyof typeof VM_TEMPLATES;

let selectedTemplate: TemplateKey | null = null;

/**
 * Shows Create VM view
 */
function showCreateVMView(user: AuthenticatedUser): void {
  document.querySelectorAll('.view').forEach((view) => view.classList.add('hidden'));
  document.getElementById('create-vm-view')?.classList.remove('hidden');

  document.querySelectorAll('.nav-item').forEach((item) => item.classList.remove('active'));
  document.getElementById('nav-create-vm')?.classList.add('active');

  // Reset wizard to step 1
  selectedTemplate = null;
  document.getElementById('vm-step-template')?.classList.remove('hidden');
  document.getElementById('vm-step-config')?.classList.add('hidden');
  document.querySelectorAll('.template-card').forEach((card) => card.classList.remove('selected'));

  setupCreateVMWizard(user);
}

/**
 * Sets up Create VM wizard event handlers
 */
function setupCreateVMWizard(_user: AuthenticatedUser): void {
  // Template selection
  for (const key of ['server', 'desktop'] as TemplateKey[]) {
    const card = document.getElementById(`tpl-${key}`);
    card?.addEventListener('click', () => selectTemplate(key));
  }

  // IP config toggle
  const ipSelect = document.getElementById('vm-ip') as unknown as HTMLSelectElement | null;
  ipSelect?.addEventListener('change', () => {
    const staticFields = document.getElementById('static-ip-fields');
    if (staticFields) {
      staticFields.classList.toggle('hidden', ipSelect.value !== 'static');
    }
  });

  // Back button
  document.getElementById('btn-back-template')?.addEventListener('click', () => {
    document.getElementById('vm-step-config')?.classList.add('hidden');
    document.getElementById('vm-step-template')?.classList.remove('hidden');
  });

  // Form submit
  const form = document.getElementById('vm-config-form') as unknown as HTMLFormElement | null;
  form?.addEventListener('submit', (e) => {
    e.preventDefault();
    submitCreateVM();
  });
}

/**
 * Handles template card selection
 */
function selectTemplate(key: TemplateKey): void {
  selectedTemplate = key;
  const tpl = VM_TEMPLATES[key];

  // Highlight selected card
  document.querySelectorAll('.template-card').forEach((card) => card.classList.remove('selected'));
  document.getElementById(`tpl-${key}`)?.classList.add('selected');

  // Set form defaults
  const memorySelect = document.getElementById('vm-memory') as unknown as HTMLSelectElement | null;
  if (memorySelect) memorySelect.value = String(tpl.defaults.memory);

  const coresSelect = document.getElementById('vm-cores') as unknown as HTMLSelectElement | null;
  if (coresSelect) coresSelect.value = String(tpl.defaults.cores);

  // Show summary
  const summary = document.getElementById('selected-template-summary');
  if (summary) {
    summary.innerHTML = `<strong>Template:</strong> ${tpl.label} — ${tpl.description}`;
  }

  // Move to step 2
  document.getElementById('vm-step-template')?.classList.add('hidden');
  document.getElementById('vm-step-config')?.classList.remove('hidden');
}

/**
 * Submits VM creation request to API
 */
async function submitCreateVM(): Promise<void> {
  if (!selectedTemplate) return;

  const tpl = VM_TEMPLATES[selectedTemplate];
  const getInputValue = (id: string, fallback: string): string => {
    const el = document.getElementById(id);
    return (el as unknown as { value: string })?.value?.trim() ?? fallback;
  };

  const name = getInputValue('vm-name', '');
  const cores = parseInt(getInputValue('vm-cores', '2'), 10);
  const memory = parseInt(getInputValue('vm-memory', '2048'), 10);
  const node = getInputValue('vm-node', 'pmhs-03');
  const ipMode = getInputValue('vm-ip', 'dhcp');

  if (!name) {
    alert('Please enter a hostname.');
    return;
  }

  const templateVmid = tpl.vmids[node];
  if (!templateVmid) {
    alert(`Template not available on ${node}`);
    return;
  }

  const btn = document.getElementById('btn-create-vm') as unknown as HTMLButtonElement | null;
  if (btn) {
    btn.disabled = true;
    btn.innerHTML = '<span class="material-icons">hourglass_empty</span> Creating...';
  }

  try {
    const body: Record<string, unknown> = {
      name,
      node,
      cores,
      memory,
      storage: 'data-zfs',
      templateVmid,
      ipConfig: ipMode === 'dhcp' ? 'ip=dhcp' : undefined,
    };

    if (ipMode === 'static') {
      const staticIp = getInputValue('vm-static-ip', '');
      const gateway = getInputValue('vm-gateway', '');
      body['ipConfig'] = `ip=${staticIp},gw=${gateway}`;
    }

    const response = await fetch('/api/vms', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const err = await response.json() as { message?: string };
      throw new Error(err.message || `API error: ${response.status}`);
    }

    const result = await response.json() as { vmid?: number; taskId?: string };
    alert(`VM created successfully!\nVM ID: ${result.vmid || 'pending'}\nTask: ${result.taskId || 'N/A'}`);

    // Reset form
    (document.getElementById('vm-config-form') as unknown as HTMLFormElement | null)?.reset();
    document.getElementById('vm-step-config')?.classList.add('hidden');
    document.getElementById('vm-step-template')?.classList.remove('hidden');
    selectedTemplate = null;
  } catch (error) {
    alert(`Failed to create VM: ${error instanceof Error ? error.message : 'Unknown error'}`);
  } finally {
    if (btn) {
      btn.disabled = false;
      btn.innerHTML = '<span class="material-icons">rocket_launch</span> Create VM';
    }
  }
}

// ============================================================================
// Application Initialization
// ============================================================================

/**
 * Initializes the application
 *
 * BPMN Process: "User Authentication and Dashboard Display"
 * Activities:
 * 1. Extract User Identity (from Cloudflare Access JWT)
 * 2. Determine User Role
 * 3. Create Authenticated Session
 * 4. Render Dashboard
 */
async function initializeApp(): Promise<void> {
  const loadingEl = document.getElementById('loading');
  const errorEl = document.getElementById('error');
  const contentEl = document.getElementById('content');
  const errorMessageEl = document.getElementById('error-message');

  try {
    // Show loading state
    loadingEl?.classList.remove('hidden');
    errorEl?.classList.add('hidden');
    contentEl?.classList.add('hidden');

    // Get current user from Cloudflare Access
    const identity = await getCurrentUser();

    // Create authenticated user with RBAC
    const user = createAuthenticatedUser(identity);

    // Log authentication (development only)
    if (import.meta.env.DEV) {
      console.warn('[DEV] User authenticated:', {
        email: user.identity.email,
        role: user.role,
        permissions: Array.from(user.permissions),
      });
    }

    // Setup UI
    renderSidebarUser(user);
    setupNavigation(user);
    showDashboard(user);

    // Show content
    loadingEl?.classList.add('hidden');
    contentEl?.classList.remove('hidden');
  } catch (error) {
    console.error('Authentication error:', error);

    // Show error state
    loadingEl?.classList.add('hidden');
    contentEl?.classList.add('hidden');
    errorEl?.classList.remove('hidden');

    if (errorMessageEl) {
      errorMessageEl.textContent =
        error instanceof Error ? error.message : 'Failed to authenticate. Please try again.';
    }
  }
}

// Initialize app when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  setBuildInfo();
  initializeApp();
});
